// Pure pipeline helpers: stage taxonomy, currency display, funnel math.
// Kept free of Supabase/React imports so they can be unit-tested in node.
import type { Database } from '@/types/database'

export type PipelineStage = Database['public']['Enums']['pipeline_stage']
// Loss reasons are per-tenant, admin-editable rows (migration 034 replaced the
// hardcoded enum with tenant_loss_reasons). The app selects a reason by id;
// labels come from the tenant's own list, never a hardcoded map.
export type TenantLossReason = Pick<
  Database['public']['Tables']['tenant_loss_reasons']['Row'],
  'id' | 'key' | 'label' | 'is_active' | 'sort_order'
>
export type PipelineGoal = Database['public']['Enums']['pipeline_goal']
export type PipelineSource = Database['public']['Enums']['pipeline_source']

// THE single source of stage order. Reads as deal progression left→right with
// the terminal states (won / lost / expired) on the right. Board columns, the
// funnel, and every stage dropdown must derive their order from this — never
// hardcode a second stage list.
export const STAGE_ORDER = [
  'pre_quote',
  'quoted',
  'on_hold',
  'won',
  'lost',
  'expired',
] as const satisfies readonly PipelineStage[]

const TERMINAL = new Set<PipelineStage>(['won', 'lost', 'expired'])

/** Terminal stages end a deal's life; leaving one is a reopen, not a move. */
export function isTerminalStage(stage: PipelineStage): boolean {
  return TERMINAL.has(stage)
}

// Derived views of STAGE_ORDER — filtering preserves the canonical order.
export const ACTIVE_STAGES: readonly PipelineStage[] = STAGE_ORDER.filter(
  (stage) => !isTerminalStage(stage),
)
export const TERMINAL_STAGES: readonly PipelineStage[] = STAGE_ORDER.filter(isTerminalStage)

/** Sorts an arbitrary stage subset (e.g. transition targets) into STAGE_ORDER. */
export function inStageOrder(stages: readonly PipelineStage[]): PipelineStage[] {
  return [...stages].sort((a, b) => STAGE_ORDER.indexOf(a) - STAGE_ORDER.indexOf(b))
}

export const STAGE_CONFIG: Record<PipelineStage, { label: string; fg: string; bg: string }> = {
  pre_quote: { label: 'Pre-quote', fg: 'var(--status-pre-quote-fg)', bg: 'var(--status-pre-quote-bg)' },
  quoted:    { label: 'Quoted',    fg: 'var(--status-quoted-fg)',    bg: 'var(--status-quoted-bg)' },
  won:       { label: 'Won',       fg: 'var(--status-won-fg)',       bg: 'var(--status-won-bg)' },
  lost:      { label: 'Lost',      fg: 'var(--status-lost-fg)',      bg: 'var(--status-lost-bg)' },
  on_hold:   { label: 'On hold',   fg: 'var(--status-on-hold-fg)',   bg: 'var(--status-on-hold-bg)' },
  expired:   { label: 'Expired',   fg: 'var(--status-expired-fg)',   bg: 'var(--status-expired-bg)' },
}

export const GOAL_LABELS: Record<PipelineGoal, string> = {
  bom_placement: 'BOM placement',
  inbound_awaiting: 'Inbound',
  early_renewal: 'Early renewal',
  cross_sell: 'Cross-sell',
  other: 'Other goal',
}

export const SOURCE_LABELS: Record<PipelineSource, string> = {
  reseller_email: 'Reseller email',
  reseller_call: 'Reseller call',
  distributor_email: 'Distributor email',
  eosl_alert: 'EOSL alert',
  proactive: 'Proactive',
  other: 'Other source',
}

// Manual stage machine. `expired` is never a manual target (it reflects a
// lapsed quote, set by data, not by a rep). Leaving won/lost/expired for an
// active stage is a REOPEN: always behind an explicit confirmation, and the
// fields the close stamped are cleared in the same patch (buildTransitionPatch).
export const STAGE_TRANSITIONS: Record<PipelineStage, readonly PipelineStage[]> = {
  pre_quote: ['quoted', 'on_hold', 'lost'],
  quoted: ['won', 'lost', 'on_hold', 'pre_quote'],
  won: ['pre_quote', 'quoted', 'on_hold'],
  lost: ['pre_quote', 'quoted', 'on_hold'],
  on_hold: ['pre_quote', 'quoted', 'lost'],
  expired: ['pre_quote', 'quoted', 'on_hold', 'lost'],
}

export interface TransitionMeta {
  /** Transition needs a revisit_date before it can commit. */
  needsRevisitDate: boolean
  /** Transition needs a loss_reason before it can commit. */
  needsLossReason: boolean
  /** Confirmation copy shown before commit; null = no confirmation step. */
  confirm: string | null
  /** Closing transitions stamp actual_close_date with today. */
  setsActualCloseDate: boolean
  /** Terminal → active move; clears the close's fields (never silent). */
  isReopen: boolean
}

/** Rules for a manual stage move, or null when the transition is invalid. */
export function describeTransition(
  from: PipelineStage,
  to: PipelineStage,
): TransitionMeta | null {
  if (!STAGE_TRANSITIONS[from].includes(to)) return null
  // expired → lost is still a close (loss dialog, close date), not a reopen.
  const isReopen = isTerminalStage(from) && !isTerminalStage(to)
  let confirm: string | null = null
  if (isReopen) {
    const target = STAGE_CONFIG[to].label.toLowerCase()
    if (from === 'won') {
      confirm = `Reopen this won deal as ${target}? The actual close date will be cleared.`
    } else if (from === 'lost') {
      confirm = `Reopen this lost deal as ${target}? The loss reason and notes will be cleared.`
    } else {
      confirm = `Reopen this expired deal as ${target}?`
    }
  } else if (from === 'quoted' && (to === 'won' || to === 'lost')) {
    confirm = `Mark this deal as ${to}? The close date will be set to today.`
  } else if (from === 'quoted' && to === 'pre_quote') {
    confirm = 'Revert this deal to pre-quote? The quote reference and line items are kept.'
  }
  return {
    needsRevisitDate: to === 'on_hold',
    needsLossReason: to === 'lost',
    confirm,
    setsActualCloseDate: to === 'won' || to === 'lost',
    isReopen,
  }
}

export interface TransitionInput {
  /** YYYY-MM-DD; required when moving to on_hold. */
  revisitDate?: string
  onHoldReason?: string
  /** tenant_loss_reasons.id; required when moving to lost. */
  lossReasonId?: string
  lossNotes?: string
}

export interface TransitionErrors {
  revisitDate?: string
  lossReason?: string
}

export type PipelineStagePatch = Pick<
  Database['public']['Tables']['pipeline']['Update'],
  'stage' | 'revisit_date' | 'on_hold_reason' | 'loss_reason_id' | 'loss_notes' | 'actual_close_date'
>

export type TransitionPlan =
  | { ok: true; patch: PipelineStagePatch }
  | { ok: false; errors: TransitionErrors }

/**
 * Validate a manual stage move and build the row patch. The patch only ever
 * touches stage/hold/loss/close fields — a quoted → pre_quote revert keeps the
 * quote ref and line items because they are never written here. A reopen
 * clears what the close stamped IN THE SAME PATCH: out of won nulls
 * actual_close_date, out of lost nulls loss_reason_id + loss_notes, out of
 * expired clears nothing (the expiry lives on the quote, not the row).
 * Throws on transitions the machine forbids: the UI must not offer them.
 */
export function buildTransitionPatch(
  from: PipelineStage,
  to: PipelineStage,
  input: TransitionInput,
  today: string,
): TransitionPlan {
  const meta = describeTransition(from, to)
  if (!meta) throw new Error(`Invalid stage transition: ${from} → ${to}`)
  const errors: TransitionErrors = {}
  if (meta.needsRevisitDate && !input.revisitDate) {
    errors.revisitDate = 'A revisit date is required to put a deal on hold.'
  }
  if (meta.needsLossReason && !input.lossReasonId) {
    errors.lossReason = 'A loss reason is required.'
  }
  if (errors.revisitDate || errors.lossReason) return { ok: false, errors }
  const patch: PipelineStagePatch = { stage: to }
  if (meta.needsRevisitDate) {
    patch.revisit_date = input.revisitDate
    if (input.onHoldReason?.trim()) patch.on_hold_reason = input.onHoldReason.trim()
  }
  if (meta.needsLossReason) {
    patch.loss_reason_id = input.lossReasonId
    if (input.lossNotes?.trim()) patch.loss_notes = input.lossNotes.trim()
  }
  if (meta.setsActualCloseDate) patch.actual_close_date = today
  if (meta.isReopen) {
    if (from === 'won') patch.actual_close_date = null
    if (from === 'lost') {
      patch.loss_reason_id = null
      patch.loss_notes = null
    }
  }
  return { ok: true, patch }
}

/** Expirations closer than this many days out render as urgent (red). */
export const EXPIRING_SOON_DAYS = 14

/**
 * Whole calendar days from `today` until `date` (both YYYY-MM-DD); negative
 * when the date is past. Date-only strings parse as UTC midnight, so the
 * difference is always an exact day count.
 */
export function daysUntil(date: string, today: string): number {
  return Math.round((Date.parse(date) - Date.parse(today)) / 86_400_000)
}

/** Whole days elapsed since an ISO timestamp, floored at zero. */
export function daysSince(timestamp: string, now: Date): number {
  return Math.max(0, Math.floor((now.getTime() - Date.parse(timestamp)) / 86_400_000))
}

/** Local calendar date as YYYY-MM-DD — close dates follow the user's clock, not UTC. */
export function localDateISO(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/** Whole-dollar display for pipeline amounts; null/undefined renders as em dash. */
export function formatCurrency(amount: number | null | undefined): string {
  if (amount == null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount)
}

export interface StageSummary {
  stage: PipelineStage
  count: number
  total: number
}

/**
 * Count + summed total_amount per stage, in the order given.
 * Stages with no rows still appear (count 0) so funnel/board columns
 * render consistently.
 */
export function summarizeByStage(
  rows: readonly { stage: PipelineStage; total_amount: number | null }[],
  stages: readonly PipelineStage[],
): StageSummary[] {
  const byStage = new Map<PipelineStage, StageSummary>(
    stages.map((stage) => [stage, { stage, count: 0, total: 0 }]),
  )
  for (const row of rows) {
    const summary = byStage.get(row.stage)
    if (!summary) continue
    summary.count += 1
    summary.total += row.total_amount ?? 0
  }
  return [...byStage.values()]
}
